import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import DashboardMetricCard from './DashboardMetricCard';

const ICAO_CLASS = {
  1: 'B',
  2: 'C',
  3: 'D',
  4: 'E',
  5: 'F',
  6: 'G',
};

function normalizeAirspaceResult(item) {
  const props = item?.properties || item || {};
  const lower = props.lowerlimit || {};
  const lowerValue = Number(props.alt_bottom ?? lower.value ?? 0);
  const lowerUnit = lower.unit === 1 ? 'ft' : (props.alt_type || 'AGL');
  const lowerDatum = lower.referenceDatum === 1 ? 'MSL' : (props.alt_type || 'AGL');
  return {
    ...props,
    cls: (props.cls || ICAO_CLASS[props.icaoclass] || '').toString().toUpperCase(),
    alt_bottom: Number.isFinite(lowerValue) ? lowerValue : 0,
    alt_type: lowerUnit === 'ft' ? lowerDatum : lowerUnit,
    relation: props.relation || (props.intersects_point || Number(props.distance_m ?? Infinity) === 0 ? 'overhead' : 'nearby'),
  };
}

const AirspaceCardDashboard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('unknown');
  const [airspaceData, setAirspaceData] = useState(null);

  useEffect(() => {
    const fetchAirspace = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['No location set']);
        setLoading(false);
        return;
      }

      // Only show loading on first load
      if (severity === 'unknown') {
        setLoading(true);
      }
      try {
        const SEARCH_RADIUS_M = 8046.72; // ~5 miles
        const res = await fetch(
          `/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=${SEARCH_RADIUS_M}&type=asp`
        );
        if (!res.ok) throw new Error(`Airspace API returned ${res.status}`);
        const data = await res.json();
        const rawResults = Array.isArray(data.airspace)
          ? data.airspace
          : Array.isArray(data.asp?.features)
          ? data.asp.features
          : [];
        const results = rawResults.map(normalizeAirspaceResult);

        if (results.length === 0) {
          // No controlled airspace - Class G
          setSeverity('go');
          onUpdate && onUpdate('go', []);
          setAirspaceData({ type: 'Class G' });
        } else {
          const overhead = results.filter(r => r.relation === 'overhead');
          const isControlled = (z) => ['B', 'C', 'D', 'E'].includes((z.cls || '').toUpperCase());
          const floorFt = (z) => {
            const floor = Number(z.alt_bottom ?? 0);
            return Number.isFinite(floor) ? floor : 0;
          };
          const floorLabel = (z) => {
            const floor = floorFt(z);
            const type = z.alt_type || 'AGL';
            return floor <= 0 ? 'Surface' : `${floor} ft ${type}`;
          };
          const summarize = (z, extra = {}) => ({
            type: z.cls ? `Class ${z.cls}` : 'Controlled',
            name: z.name,
            floor: floorLabel(z),
            floorFt: floorFt(z),
            requirements: extra.requirements,
            droneImpact: extra.droneImpact,
          });

          if (overhead.length > 0) {
            const controlledOverhead = overhead.filter(isControlled);
            if (controlledOverhead.length > 0) {
              const sorted = [...controlledOverhead].sort((a, b) => floorFt(a) - floorFt(b));
              const zone = sorted[0];
              const cls = zone.cls;
              if (floorFt(zone) > 400) {
                setSeverity('go');
                onUpdate && onUpdate('go', []);
                setAirspaceData({
                  type: 'Class G',
                  name: zone.name,
                  floor: floorLabel(zone),
                  floorFt: floorFt(zone),
                  droneImpact: `Class ${cls} shelf starts above normal drone ceiling`,
                  requirements: 'Uncontrolled below the overlying shelf for normal small UAS altitude.',
                });
              } else {
                setSeverity('no-go');
                onUpdate && onUpdate('no-go', [`Class ${cls} airspace at ${floorLabel(zone)} - authorization required`]);
                setAirspaceData(summarize(zone, {
                  droneImpact: 'At or below drone ceiling',
                  requirements: 'Authorization required before flight.',
                }));
              }
            } else {
              setSeverity('go');
              onUpdate && onUpdate('go', []);
              setAirspaceData({ type: 'Class G' });
            }
          } else {
            const nearbyControlled = results.filter(isControlled).sort((a, b) => (a.distance_m ?? Infinity) - (b.distance_m ?? Infinity));
            const zone = nearbyControlled[0] || results[0];
            setSeverity('go');
            onUpdate && onUpdate('go', []);
            setAirspaceData({
              type: 'Class G',
              name: zone?.name,
              droneImpact: zone?.relation === 'nearby' ? 'Controlled airspace nearby, not overhead' : undefined,
            });
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Airspace fetch failed:', err);
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['Airspace data unavailable']);
        setLoading(false);
      }
    };

    fetchAirspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  const primaryMetric = airspaceData ? {
    value: airspaceData.type || 'Unknown',
    label: 'Airspace Classification'
  } : {
    value: 'Class G',
    label: 'Uncontrolled Airspace'
  };

  const secondaryMetrics = airspaceData ? [
    airspaceData.floor && { label: 'Floor', value: airspaceData.floor, highlight: airspaceData.floorFt <= 400 },
    airspaceData.droneImpact && { label: 'Drone impact', value: airspaceData.droneImpact }
  ].filter(Boolean) : [];

  const isClassG = (airspaceData?.type || '').toUpperCase() === 'CLASS G';
  const statusMessage = severity === 'go'
    ? 'Class G uncontrolled airspace'
    : severity === 'advisory'
    ? 'Controlled airspace nearby'
    : severity === 'caution'
    ? 'Controlled airspace nearby'
    : severity === 'no-go'
    ? 'Check LAANC authorization'
    : 'Checking airspace...';

  return (
    <DashboardMetricCard
      title="Airspace"
      icon={FlightIcon}
      status={severity}
      primaryMetric={primaryMetric}
      secondaryMetrics={secondaryMetrics}
      statusMessage={statusMessage}
      loading={loading}
      minHeight={112}
    >
      {airspaceData && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Airspace Details
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {airspaceData.name && (
              <Typography variant="body2">
                <strong>Name:</strong> {airspaceData.name}
              </Typography>
            )}
            {airspaceData.type && (
              <Chip
                label={airspaceData.type}
                size="small"
                color={severity === 'no-go' ? 'error' : severity === 'caution' ? 'warning' : isClassG ? 'success' : 'default'}
              />
            )}
            {airspaceData.requirements && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {airspaceData.requirements}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </DashboardMetricCard>
  );
};

export default AirspaceCardDashboard;
